// `VerifyPendingPage` — US-002 + US-003 ("we sent you a link" + Resend).
//
// DESIGN.md §1.3, §4.3, O2 (button label flips during cooldown).

import { useEffect, useId, useState } from 'react';

import { ApiError, useMe, useResendVerification } from '@barel/api-client/react';
import { Trans, useT } from '@barel/i18n';
import { Banner, Button, toast } from '@barel/ui';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';

import { AuthLayout } from './AuthLayout';
import { emit } from './lib/telemetry';
import { useResendCooldown } from './lib/useResendCooldown';

interface VerifyPendingSearch {
  email?: string;
}

/** Mask the address as `j***@gmail.com` for the body banner. */
function maskEmail(email: string | undefined): string | undefined {
  if (!email) return undefined;
  const [local, domain] = email.split('@');
  if (!local || !domain) return email;
  const visible = local.slice(0, 1);
  return `${visible}${'*'.repeat(Math.max(2, local.length - 1))}@${domain}`;
}

export function VerifyPendingPage() {
  const t = useT('auth');
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as VerifyPendingSearch;
  const me = useMe({ enabled: true, retry: false });
  const resend = useResendVerification();

  const cooldown = useResendCooldown(`verify-cooldown:${search.email ?? 'self'}`);

  // Already-verified short-circuit.
  useEffect(() => {
    if (me.data?.user?.emailVerified) {
      void navigate({ to: '/', replace: true });
    }
  }, [me.data?.user?.emailVerified, navigate]);

  const [bannerError, setBannerError] = useState<string | null>(null);

  const handleResend = async () => {
    setBannerError(null);
    emit({ name: 'auth_verify_resend_clicked' });
    if (!search.email) {
      setBannerError(t('verify.pending.errors.generic'));
      return;
    }
    try {
      await resend.mutateAsync({ email: search.email });
      cooldown.start(60_000);
      toast.success({ title: t('verify.pending.resentToast') });
    } catch (err) {
      if (ApiError.is(err) && err.httpStatus === 429) {
        setBannerError(t('verify.pending.errors.dailyLimit'));
        return;
      }
      setBannerError(t('verify.pending.errors.generic'));
    }
  };

  const titleId = useId();
  const masked = maskEmail(search.email);

  const cooldownLabel = cooldown.active
    ? t('verify.pending.cooldownCta', { seconds: cooldown.remaining })
    : t('verify.pending.resendCta');

  return (
    <AuthLayout brandHeadline="brandHeadlineNeutral">
      <section
        data-route="auth/verify-pending"
        aria-labelledby={titleId}
        className="flex flex-col gap-6"
      >
        <header className="flex flex-col gap-2">
          <h1 id={titleId} className="text-xl font-semibold text-primary">
            {t('verify.pending.title')}
          </h1>
          <p className="text-sm leading-relaxed text-secondary">
            {masked ? (
              <span data-testid="verify-pending-body">
                <Trans
                  ns="auth"
                  i18nKey="verify.pending.body"
                  values={{ email: masked }}
                  components={{ strong: <strong /> }}
                />
              </span>
            ) : (
              <span data-testid="verify-pending-body-unknown">
                {t('verify.pending.bodyUnknown')}
              </span>
            )}
          </p>
        </header>

        {bannerError ? <Banner tone="warning">{bannerError}</Banner> : null}

        <div className="flex flex-col gap-3">
          <Button
            type="button"
            variant="primary"
            size="lg"
            fullWidth
            loading={resend.isPending}
            disabled={cooldown.active || resend.isPending}
            onClick={() => {
              void handleResend();
            }}
            data-testid="verify-pending-resend"
          >
            {cooldownLabel}
          </Button>
        </div>

        <p className="text-sm text-secondary">
          <Link
            to="/auth/signup"
            className="underline hover:text-primary"
            data-testid="verify-pending-change-email"
          >
            {t('verify.pending.changeEmail')}
          </Link>
        </p>
      </section>
    </AuthLayout>
  );
}
